/**
 * Tree-level Legacy Book — full archive keepsake layout for HTML preview / future print export.
 */
import React, { useMemo, useState } from 'react';
import { ExternalLink, FileText, Heart, Mic, PawPrint, User, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getBackendBaseUrl } from '../../utils/apiBase';
import { formatPersonDateRange } from '../../utils/dateDisplay';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { personHref } from '../../utils/prettyArchiveLinks';
import {
  TREE_BOOK_COPY,
  formatPersonCounts,
  getPersonRoleLabel,
  partitionPeopleForBook,
  peopleOverviewTitle,
} from '../../utils/treeLegacyBookDisplay';
import { isCompanionProfile } from '../../utils/companionProfiles';
import { cn } from '@/lib/utils';

function ProfilePhoto({ url, name, className, backendBase, size = 'default' }) {
  const [broken, setBroken] = useState(false);
  const src = resolveAssetUrl(backendBase, url);
  const initials = (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

  const showImage = src && !broken;

  if (showImage) {
    return (
      <img
        src={src}
        alt={name}
        className={className}
        loading="lazy"
        onError={() => setBroken(true)}
      />
    );
  }

  return (
    <div
      className={cn(
        'legacy-book-profile-fallback flex items-center justify-center',
        size === 'large' && 'legacy-book-profile-fallback--large',
        className,
      )}
      aria-hidden
    >
      <span className="legacy-book-profile-initials">{initials || <User className="h-8 w-8" />}</span>
    </div>
  );
}

function DocPhoto({ doc, backendBase, featured = false }) {
  const [broken, setBroken] = useState(false);
  const src = doc.is_image && doc.file_url ? resolveAssetUrl(backendBase, doc.file_url) : null;

  if (!doc.is_image || !doc.file_url) {
    return (
      <div className="legacy-book-doc-placeholder">
        <FileText className="legacy-book-doc-placeholder-icon" aria-hidden />
        <span>{doc.title || 'Document'}</span>
      </div>
    );
  }

  if (!src || broken) {
    return (
      <div className="legacy-book-doc-placeholder">
        <span className="legacy-book-doc-placeholder-label">{doc.title || 'Photo'}</span>
        <span className="legacy-book-doc-placeholder-hint">Photo can be added later</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={doc.title || 'Photo'}
      loading="lazy"
      className={cn('legacy-book-doc-image', featured && 'legacy-book-doc-image--featured')}
      onError={() => setBroken(true)}
    />
  );
}

function MemoryBlock({ memory }) {
  const title = memory.display_title || 'A cherished memory';
  const body = memory.body;
  const showQuestion =
    memory.question_text &&
    memory.question_text !== title &&
    !body?.trim().startsWith(memory.question_text.trim().slice(0, 40));

  return (
    <article className="legacy-book-story-card" data-testid={`tree-book-memory-${memory.id}`}>
      <h4 className="legacy-book-story-title">{title}</h4>
      {showQuestion ? <p className="legacy-book-story-epigraph">{memory.question_text}</p> : null}
      {body ? (
        <div className="legacy-book-story-body whitespace-pre-wrap">{body}</div>
      ) : memory.has_voice ? (
        <p className="legacy-book-story-body legacy-book-story-body--voice">
          A voice memory is preserved in your archive — the spoken words can be transcribed when you are ready.
        </p>
      ) : (
        <p className="legacy-book-story-body legacy-book-story-body--placeholder">
          A memory started — open the archive to continue writing.
        </p>
      )}
      <footer className="legacy-book-story-footer">
        {memory.source_date ? <span>{memory.source_date}</span> : null}
        {memory.has_voice && !body ? (
          <span className="legacy-book-story-voice">
            <Mic className="h-3 w-3" aria-hidden />
            Voice preserved
          </span>
        ) : null}
        {memory.has_voice && body ? (
          <span className="legacy-book-story-voice legacy-book-story-voice--quiet">
            <Mic className="h-3 w-3" aria-hidden />
            Originally spoken
          </span>
        ) : null}
      </footer>
    </article>
  );
}

function PersonOverviewRow({ person, backendBase }) {
  const years = formatPersonDateRange(person);
  const roleLabel = person.profile_role_label || getPersonRoleLabel(person);
  const counts = formatPersonCounts(person.counts);

  return (
    <li className="legacy-book-overview-row">
      <div className="legacy-book-overview-avatar">
        <ProfilePhoto
          url={person.profile_image_url}
          name={person.display_name}
          className="h-full w-full object-cover"
          backendBase={backendBase}
        />
      </div>
      <div className="legacy-book-overview-text">
        <p className="legacy-book-overview-name">{person.display_name}</p>
        <p className="legacy-book-overview-meta">
          {[years, roleLabel, counts].filter(Boolean).join(' · ')}
        </p>
      </div>
    </li>
  );
}

function PersonChapter({ person, chapterIndex, backendBase }) {
  const years = formatPersonDateRange(person);
  const profileHref = personHref(person);
  const hasMemories = person.memories?.length > 0;
  const hasDocs = person.documents?.length > 0;
  const photoDocs = (person.documents || []).filter((d) => d.is_image);
  const otherDocs = (person.documents || []).filter((d) => !d.is_image);
  const isCompanion = isCompanionProfile(person);
  const roleLabel = person.profile_role_label || getPersonRoleLabel(person);
  const counts = formatPersonCounts(person.counts);
  const chapterLabel = isCompanion ? 'Companion chapter' : 'Chapter';

  return (
    <article
      className={cn(
        'legacy-book-page legacy-book-page--chapter',
        isCompanion && 'legacy-book-page--companion-chapter',
      )}
      data-testid={`tree-book-person-${person.id}`}
    >
      <header className="legacy-book-chapter-header">
        <p className="legacy-book-chapter-number">
          {chapterLabel} {chapterIndex}
        </p>
        <div className="legacy-book-chapter-opener">
          <div className="legacy-book-chapter-portrait">
            <ProfilePhoto
              url={person.profile_image_url}
              name={person.display_name}
              className="h-full w-full object-cover"
              backendBase={backendBase}
              size="large"
            />
          </div>
          <div className="legacy-book-chapter-intro">
            {roleLabel ? (
              <span className={cn('legacy-book-role-badge', isCompanion && 'legacy-book-role-badge--companion')}>
                {isCompanion ? <PawPrint className="h-3 w-3" aria-hidden /> : null}
                {roleLabel}
              </span>
            ) : null}
            <h2 className="legacy-book-chapter-name">{person.display_name}</h2>
            {years ? <p className="legacy-book-years">{years}</p> : null}
            {person.key_place ? <p className="legacy-book-chapter-place">{person.key_place}</p> : null}
            {counts ? <p className="legacy-book-chapter-counts">{counts}</p> : null}
          </div>
        </div>
      </header>

      <section className="legacy-book-chapter-section">
        {person.biographical_notes ? (
          <p className="legacy-book-body whitespace-pre-wrap">{person.biographical_notes}</p>
        ) : (
          <p className="legacy-book-body legacy-book-body--encouraging">{TREE_BOOK_COPY.emptyBio}</p>
        )}
        {person.life_themes?.length > 0 ? (
          <div className="legacy-book-theme-row">
            {person.life_themes.map((theme) => (
              <span key={theme} className="legacy-book-theme-chip">
                {theme}
              </span>
            ))}
          </div>
        ) : null}
        {person.featured_memory_excerpt ? (
          <blockquote className="legacy-book-featured-quote">{person.featured_memory_excerpt}</blockquote>
        ) : null}
        <p className="legacy-book-chapter-link">
          <Link to={profileHref}>View profile in archive</Link>
        </p>
      </section>

      <section className="legacy-book-chapter-section legacy-book-chapter-section--stories">
        <p className="legacy-book-section-label">Stories</p>
        <h3 className="legacy-book-section-title legacy-book-section-title--small">
          Memories of {person.display_name}
        </h3>
        {!hasMemories ? (
          <p className="legacy-book-body legacy-book-body--encouraging">{TREE_BOOK_COPY.emptyMemories}</p>
        ) : (
          <div className="legacy-book-stories-stack">
            {person.memories.map((m) => (
              <MemoryBlock key={m.id} memory={m} />
            ))}
          </div>
        )}
      </section>

      <section className="legacy-book-chapter-section legacy-book-chapter-section--photos">
        <p className="legacy-book-section-label">Photographs</p>
        {!hasDocs ? (
          <p className="legacy-book-body legacy-book-body--encouraging">{TREE_BOOK_COPY.emptyPhotos}</p>
        ) : (
          <>
            {photoDocs.length > 0 ? (
              <div
                className={cn(
                  'legacy-book-photo-gallery',
                  photoDocs.length === 1 && 'legacy-book-photo-gallery--single',
                )}
              >
                {photoDocs.map((doc, idx) => (
                  <figure
                    key={doc.id}
                    className={cn(
                      'legacy-book-photo-frame',
                      idx === 0 && photoDocs.length > 1 && 'legacy-book-photo-frame--lead',
                    )}
                  >
                    <DocPhoto doc={doc} backendBase={backendBase} featured={idx === 0} />
                    {(doc.title || doc.description) && (
                      <figcaption className="legacy-book-photo-caption">
                        {doc.title}
                        {doc.description ? (
                          <span className="legacy-book-photo-caption-detail"> — {doc.description}</span>
                        ) : null}
                      </figcaption>
                    )}
                  </figure>
                ))}
              </div>
            ) : null}
            {otherDocs.length > 0 ? (
              <ul className="legacy-book-doc-list">
                {otherDocs.map((doc) => (
                  <li key={doc.id}>
                    <FileText className="h-4 w-4 shrink-0" aria-hidden />
                    <span>
                      {doc.title}
                      {doc.document_date ? ` · ${doc.document_date}` : ''}
                      {doc.description ? ` — ${doc.description}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </section>
    </article>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="legacy-book-stat-tile">
      <p className="legacy-book-stat-value">{value ?? 0}</p>
      <p className="legacy-book-stat-label">{label}</p>
    </div>
  );
}

export function TreeLegacyBookPrintLayout({ data }) {
  const backendBase = getBackendBaseUrl();
  const tree = data?.tree || {};
  const people = useMemo(() => data?.people || [], [data?.people]);
  const stats = data?.stats || {};

  const { lovedOnes, companions } = useMemo(() => partitionPeopleForBook(people), [people]);
  const overviewTitle = peopleOverviewTitle(lovedOnes, companions);

  const generatedDate = useMemo(() => {
    try {
      return new Date(data.generated_at).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return new Date().toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }
  }, [data?.generated_at]);

  const coverUrl = resolveAssetUrl(backendBase, tree.cover_image_url);
  const showPhotoCover = Boolean(coverUrl);

  const lovedOnesLabel =
    stats.companions_count > 0
      ? `${stats.family_members_count ?? lovedOnes.length} loved ${
          (stats.family_members_count ?? lovedOnes.length) === 1 ? 'one' : 'ones'
        }`
      : `${stats.people_count} ${stats.people_count === 1 ? 'loved one' : 'loved ones'}`;

  return (
    <div className="tree-legacy-book-content" data-testid="tree-legacy-book-print-layout">
      {/* Cover */}
      <article
        className={cn(
          'legacy-book-page legacy-book-page--cover legacy-book-page--tree-cover',
          showPhotoCover && 'legacy-book-page--cover-photo',
        )}
        style={showPhotoCover ? { backgroundImage: `url(${coverUrl})` } : undefined}
        data-testid="tree-book-cover"
      >
        <div className="legacy-book-cover-ornament" aria-hidden />
        <div className="legacy-book-cover-inner">
          <p className="legacy-book-cover-eyebrow">Legacy Book</p>
          <h1 className="legacy-book-name">{tree.name || 'Family Archive'}</h1>
          <p className="legacy-book-subtitle">{TREE_BOOK_COPY.coverSubtitle}</p>
          <p className="legacy-book-cover-tagline">{TREE_BOOK_COPY.coverTagline}</p>
          {stats.people_count > 0 || stats.memories_count > 0 ? (
            <p className="legacy-book-cover-stats">
              {stats.people_count > 0 ? lovedOnesLabel : null}
              {stats.companions_count > 0
                ? `${stats.people_count > 0 ? ' · ' : ''}${stats.companions_count} ${
                    stats.companions_count === 1 ? 'companion' : 'companions'
                  }`
                : null}
              {stats.memories_count > 0
                ? `${stats.people_count > 0 || stats.companions_count > 0 ? ' · ' : ''}${stats.memories_count} ${
                    stats.memories_count === 1 ? 'memory' : 'memories'
                  }`
                : null}
            </p>
          ) : null}
          <p className="legacy-book-cover-date">Created {generatedDate}</p>
          <p className="legacy-book-cover-attribution">Prepared with MyLegacyBridge</p>
        </div>
      </article>

      {/* Opening message */}
      <article className="legacy-book-page legacy-book-page--dedication" data-testid="tree-book-opening">
        <div className="legacy-book-dedication-ornament" aria-hidden>
          <Heart className="h-5 w-5" />
        </div>
        <p className="legacy-book-section-label">Opening message</p>
        <h2 className="legacy-book-dedication-title">Preserved for future generations</h2>
        <p className="legacy-book-dedication-lead">{TREE_BOOK_COPY.openingLead}</p>
        <p className="legacy-book-dedication-gentle">{TREE_BOOK_COPY.openingGentle}</p>
        <p className="legacy-book-dedication-promise">{TREE_BOOK_COPY.promise}</p>
      </article>

      {/* Archive introduction */}
      <article className="legacy-book-page legacy-book-page--intro" data-testid="tree-book-intro">
        <p className="legacy-book-section-label">Your archive</p>
        <h2 className="legacy-book-section-title">{tree.name}</h2>
        {tree.description ? (
          <p className="legacy-book-body whitespace-pre-wrap">{tree.description}</p>
        ) : (
          <p className="legacy-book-body legacy-book-body--encouraging">
            A private family space for the people, memories and keepsakes you choose to preserve — before memories
            fade.
          </p>
        )}
        <div className="legacy-book-stats-grid">
          <StatTile
            label={stats.companions_count > 0 ? 'Loved ones & companions' : 'Loved ones'}
            value={stats.people_count}
          />
          <StatTile label="Memories" value={stats.memories_count} />
          <StatTile label="Voice memories" value={stats.voice_memories_count} />
          <StatTile label="Photos & documents" value={stats.documents_count} />
        </div>
      </article>

      {/* People overview */}
      <article className="legacy-book-page" data-testid="tree-book-people-overview">
        <p className="legacy-book-section-label">Family overview</p>
        <h2 className="legacy-book-section-title">{overviewTitle}</h2>
        {people.length === 0 ? (
          <p className="legacy-book-body legacy-book-body--encouraging">{TREE_BOOK_COPY.emptyPeople}</p>
        ) : (
          <>
            {lovedOnes.length > 0 ? (
              <div className="legacy-book-overview-group">
                {companions.length > 0 ? (
                  <h3 className="legacy-book-overview-group-title">
                    <Users className="h-4 w-4" aria-hidden />
                    Loved ones
                  </h3>
                ) : null}
                <ul className="legacy-book-overview-list">
                  {lovedOnes.map((person) => (
                    <PersonOverviewRow key={person.id} person={person} backendBase={backendBase} />
                  ))}
                </ul>
              </div>
            ) : null}
            {companions.length > 0 ? (
              <div className={cn('legacy-book-overview-group', lovedOnes.length > 0 && 'mt-8')}>
                <h3 className="legacy-book-overview-group-title">
                  <PawPrint className="h-4 w-4" aria-hidden />
                  Companions
                </h3>
                <ul className="legacy-book-overview-list">
                  {companions.map((person) => (
                    <PersonOverviewRow key={person.id} person={person} backendBase={backendBase} />
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </article>

      {/* Person chapters */}
      {people.map((person, idx) => (
        <PersonChapter key={person.id} person={person} chapterIndex={idx + 1} backendBase={backendBase} />
      ))}

      {/* Possessions */}
      {data.possessions?.length > 0 ? (
        <article className="legacy-book-page" data-testid="tree-book-possessions">
          <p className="legacy-book-section-label">Keepsakes</p>
          <h2 className="legacy-book-section-title">Priceless possessions</h2>
          <ul className="legacy-book-keepsakes-list">
            {data.possessions.map((item) => (
              <li key={item.id} className="legacy-book-keepsake-card">
                <p className="legacy-book-keepsake-title">{item.title}</p>
                {item.connected_people?.length ? (
                  <p className="legacy-book-keepsake-meta">Connected to {item.connected_people.join(', ')}</p>
                ) : null}
                {(item.why_it_matters || item.story_text || item.description) && (
                  <p className="legacy-book-body">{item.why_it_matters || item.story_text || item.description}</p>
                )}
              </li>
            ))}
          </ul>
        </article>
      ) : null}

      {/* Timeline */}
      {data.timeline_moments?.length > 0 ? (
        <article className="legacy-book-page" data-testid="tree-book-timeline">
          <p className="legacy-book-section-label">Through time</p>
          <h2 className="legacy-book-section-title">Key moments</h2>
          <ul className="legacy-book-timeline">
            {data.timeline_moments.map((moment, idx) => (
              <li key={`${moment.kind}-${moment.person_id}-${idx}`} className="legacy-book-timeline-item">
                <span className="legacy-book-timeline-date">{moment.date_display || moment.date}</span>
                <div className="legacy-book-timeline-content">
                  <p className="legacy-book-timeline-person">
                    {moment.person_name}
                    {moment.kind === 'memory' ? '' : ` — ${moment.label}`}
                  </p>
                  {moment.kind === 'memory' ? (
                    <p className="legacy-book-timeline-detail">{moment.label}</p>
                  ) : null}
                  {moment.place ? <p className="legacy-book-timeline-detail">{moment.place}</p> : null}
                </div>
              </li>
            ))}
          </ul>
        </article>
      ) : (
        <article className="legacy-book-page legacy-book-page--soft" data-testid="tree-book-timeline-empty">
          <p className="legacy-book-section-label">Through time</p>
          <h2 className="legacy-book-section-title">Key moments</h2>
          <p className="legacy-book-body legacy-book-body--encouraging">{TREE_BOOK_COPY.emptyTimeline}</p>
        </article>
      )}

      {/* Memorial links */}
      {data.published_memorials?.length > 0 ? (
        <article className="legacy-book-page" data-testid="tree-book-memorials">
          <p className="legacy-book-section-label">Remembered with love</p>
          <h2 className="legacy-book-section-title">Published memorial pages</h2>
          <ul className="legacy-book-memorial-list">
            {data.published_memorials.map((m) => (
              <li key={m.slug || m.person_id}>
                <a href={m.public_url} target="_blank" rel="noopener noreferrer">
                  {m.person_name}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </a>
              </li>
            ))}
          </ul>
        </article>
      ) : null}

      {/* Closing */}
      <article className="legacy-book-page legacy-book-page--closing" data-testid="tree-book-closing">
        <div className="legacy-book-closing-inner">
          <div className="legacy-book-closing-ornament" aria-hidden />
          <h2 className="legacy-book-dedication-title">Created with love</h2>
          <p className="legacy-book-closing-lead">{TREE_BOOK_COPY.closingLead}</p>
          <p className="legacy-book-closing-gentle">{TREE_BOOK_COPY.openingGentle}</p>
          <p className="legacy-book-closing-archive">{tree.name}</p>
          <p className="legacy-book-closing-date">Created {generatedDate}</p>
          {tree.legacy_book_qr_data_url ? (
            <div className="legacy-book-online-qr" data-testid="tree-book-online-qr">
              <img src={tree.legacy_book_qr_data_url} alt="" width={96} height={96} />
              <p className="legacy-book-online-qr-label">View the online Legacy Book</p>
              <p className="legacy-book-online-qr-hint">Sign in to your family archive to open this keepsake preview.</p>
            </div>
          ) : null}
          <p className="legacy-book-cover-attribution">MyLegacyBridge</p>
        </div>
      </article>
    </div>
  );
}

export default TreeLegacyBookPrintLayout;
